/**
 * Builds the geometry of the whole chart, as plain data. No DOM here: every
 * decision below is a pure function of the document and the metrics, which is
 * what makes the awkward cases (band breaks, uncertainty, narrow bars) testable
 * without a browser.
 */

import {
  isEvent,
  isPeriod,
  itemExtent,
  type Band,
  type ChronoDocument,
  type EventItem,
  type Item,
  type PeriodItem,
} from '../model/types';
import { formatYearValue } from '../model/epoch';
import { assignLanes, laneCount } from './lanes';
import { axisHeight as axisHeightFor, MIN_BAR_PX, MIN_WHISKER_PX, type Metrics } from './metrics';
import { scaleForBandWidth, scaleForTickWidth, type Scale } from './scale';
import { approxLabelWidth, buildTicks, type Tick } from './ticks';

/** An entry paired with its position in `doc.items`, so the UI can address it. */
interface Indexed<T> {
  item: T;
  index: number;
}

export interface BarLayout {
  item: PeriodItem;
  /** Index in `doc.items`; what selection and editing act on. */
  index: number;
  lane: number;
  x: number;
  width: number;
  /** The bar leaves the band: drawn with the mockup's cut gradient. */
  cutStart: boolean;
  cutEnd: boolean;
  /** Dotted guide up to the axis, when the bound falls inside the band (features.md §6bis). */
  guideStart: number | null;
  guideEnd: number | null;
  label: string;
  /**
   * Which way an overflowing label runs. A bar narrower than its text lets the
   * text spill (features.md §2) — but near the end of a band that spill leaves
   * the chart, so there the label ends on the bar instead of starting on it.
   */
  labelAnchor: 'start' | 'end';
}

export interface EventLayout {
  item: EventItem;
  index: number;
  lane: number;
  x: number;
  /**
   * What the marker reads, dates included. Every point event states its exact
   * year and every uncertain one states its two bounds — above the axis line,
   * where nothing competes with the tick labels drawn underneath it.
   */
  text: string;
  /** Same rule as a bar's label: near the band's end, the marker reads leftwards. */
  labelAnchor: 'start' | 'end';
  /** Uncertainty range, when wide enough at this scale to mean anything. */
  whisker: { x: number; width: number } | null;
}

export interface BandLayout {
  band: Band;
  scale: Scale;
  ticks: Tick[];
  bars: BarLayout[];
  events: EventLayout[];
  width: number;
  eventsHeight: number;
  axisHeight: number;
  periodsHeight: number;
  height: number;
}

export interface LayoutOptions {
  metrics: Metrics;
  measureLabel?: (text: string) => number;
  /** Overrides the screen scale; phase 6 passes the usable page width here. */
  bandWidth?: (band: Band) => number;
  /** Category filters. Applied before stacking, so hidden rows leave no gap. */
  isVisible?: (item: Item) => boolean;
}

export interface TimelineLayout {
  bands: BandLayout[];
}

const overlapsBand = (item: PeriodItem, band: Band): boolean => {
  const [start, end] = itemExtent(item);
  return end >= band.start && start <= band.end;
};

/**
 * Dates are written in the band's scale but without its unit: on a prehistoric
 * band an event reads "3,18 · LUCY", and the axis right below it is what says
 * "Ma AP". Repeating the unit on every marker would cost more room than it buys.
 */
function eventText(item: EventItem, unit: Band['unit']): string {
  const date = item.span
    ? `${formatYearValue(item.span[0], unit)} à ${formatYearValue(item.span[1], unit)}`
    : formatYearValue(item.year, unit);
  return `${date} · ${item.label}`;
}

export function layoutDocument(doc: ChronoDocument, options: LayoutOptions): TimelineLayout {
  const measure = options.measureLabel ?? approxLabelWidth;

  const visible = doc.items
    .map((item, index) => ({ item, index }))
    .filter((entry) => options.isVisible?.(entry.item) ?? true);

  const periods = visible.filter((entry): entry is Indexed<PeriodItem> => isPeriod(entry.item));
  const events = visible.filter((entry): entry is Indexed<EventItem> => isEvent(entry.item));

  // Global, so a period keeps its lane across a band break.
  const periodLanes = assignLanes(periods, {
    extent: (entry) => itemExtent(entry.item),
    key: (entry) => entry.item.label,
    pinned: (entry) => entry.item.lane,
  });

  const bands = doc.bands.map((band, index) => {
    const scale = options.bandWidth
      ? scaleForBandWidth(band, options.bandWidth(band))
      : scaleForTickWidth(band, options.metrics.pxPerTick);

    const bandEvents = events.filter(
      (entry) => entry.item.year >= band.start && entry.item.year <= band.end,
    );

    /** Does this marker's text still fit to the right of its own position? */
    const eventAnchor = (item: EventItem): 'start' | 'end' =>
      scale.x(item.year) + measure(eventText(item, band.unit)) > scale.width ? 'end' : 'start';

    // Event lanes are per band: they depend on label width, hence on the scale,
    // and points need no continuity across a break.
    const eventLanes = assignLanes(bandEvents, {
      extent: (entry) => {
        const [from, to] = itemExtent(entry.item);
        // Measured on the rendered text, dates included, or the anti-collision
        // would be computed on a shorter label than the one actually drawn —
        // and on the side the text actually runs, which flips near the end.
        const labelYears = measure(eventText(entry.item, band.unit)) / scale.pxPerYear;
        return eventAnchor(entry.item) === 'end'
          ? [Math.min(from, entry.item.year - labelYears), to]
          : [from, Math.max(to, entry.item.year + labelYears)];
      },
      key: (entry) => `${entry.item.year}:${entry.item.label}`,
      pinned: (entry) => entry.item.lane,
    });

    const bars = periods
      .filter((entry) => overlapsBand(entry.item, band))
      .map((entry) =>
        buildBar(entry, band, scale, periodLanes.get(entry) ?? 0, doc.bands, index, measure),
      );

    // Only period bounds ask for their two surrounding graduations. A bar shows
    // no dates of its own, so the axis is the only way to place it; a point
    // event states its date in its own label and needs nothing from the scale.
    const anchors = bars.flatMap((bar) => [bar.item.start, bar.item.end]);

    const ticks = buildTicks(band, scale, anchors, measure);

    const eventLayouts = bandEvents.map<EventLayout>((entry) => ({
      item: entry.item,
      index: entry.index,
      lane: eventLanes.get(entry) ?? 0,
      x: scale.x(entry.item.year),
      text: eventText(entry.item, band.unit),
      labelAnchor: eventAnchor(entry.item),
      whisker: buildWhisker(entry.item, band, scale),
    }));

    const eventsHeight = laneCount(eventLanes) * options.metrics.eventLaneH;
    const axisHeight = axisHeightFor(options.metrics);
    const periodLaneSpan = bars.reduce((max, bar) => Math.max(max, bar.lane + 1), 0);
    const periodsHeight = periodLaneSpan * options.metrics.periodLaneH;

    return {
      band,
      scale,
      ticks,
      bars,
      events: eventLayouts,
      width: scale.width,
      eventsHeight,
      axisHeight,
      periodsHeight,
      // The band title sits above the body, on its own line.
      height: options.metrics.bandLabelH + eventsHeight + axisHeight + periodsHeight,
    };
  });

  return { bands };
}

function buildBar(
  entry: Indexed<PeriodItem>,
  band: Band,
  scale: Scale,
  lane: number,
  allBands: readonly Band[],
  bandIndex: number,
  measure: (text: string) => number,
): BarLayout {
  const { item, index } = entry;
  const cutStart = item.start < band.start;
  const cutEnd = item.end > band.end;

  const left = scale.x(Math.max(item.start, band.start));
  const right = scale.x(Math.min(item.end, band.end));
  const width = Math.max(MIN_BAR_PX, right - left);

  // "(suite)" only makes sense if an earlier band actually showed the bar; a
  // period starting before the very first band has nothing to continue from.
  const continued =
    cutStart && allBands.slice(0, bandIndex).some((earlier) => overlapsBand(item, earlier));

  const label = continued ? `← ${item.label} (suite)` : item.label;
  const labelWidth = measure(label);

  return {
    item,
    index,
    lane,
    x: left,
    width,
    cutStart,
    cutEnd,
    guideStart: cutStart ? null : scale.x(item.start),
    guideEnd: cutEnd ? null : scale.x(item.end),
    label,
    // Only worth flipping when the text actually spills: a label that fits
    // inside its bar is centred there either way.
    labelAnchor: labelWidth > width && left + labelWidth > scale.width ? 'end' : 'start',
  };
}

function buildWhisker(item: EventItem, band: Band, scale: Scale): EventLayout['whisker'] {
  if (!item.span) return null;

  const from = Math.max(item.span[0], band.start);
  const to = Math.min(item.span[1], band.end);
  const x = scale.x(from);
  const width = scale.x(to) - x;

  return width >= MIN_WHISKER_PX ? { x, width } : null;
}
